import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
    TableSortLabel,
    Snackbar,
    Alert,
    TablePagination,
    Chip,
    InputAdornment,
    MenuItem
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Store as StoreIcon, // Icon for Parceiros
    Search as SearchIcon,
    Print as PrintIcon
} from "@mui/icons-material";
import { useAuth } from '../../hooks/useAuth.js';
import api from '../../services/api.js';
import Preloader from '../../components/Preloader';
import CadastroParceiro from './CadastroParceiro';
import { formatarCNPJ, formatarCPF, formatarTelefone } from '../../utils/formatadores.js';

export default function ListaParceiros() {
    const [parceiros, setParceiros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Dialog Control
    const [openCadastro, setOpenCadastro] = useState(false);
    const [parceiroEditarId, setParceiroEditarId] = useState(null);

    // Filters
    const [busca, setBusca] = useState("");
    const [filtroCategoria, setFiltroCategoria] = useState("");

    // Pagination & Sort
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("st_nome");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const categorias = ['CLINICA', 'LABORATORIO', 'CONSULTORIO', 'HOSPITAL', 'FARMACIA', 'OTICA', 'OUTRO'];

    useEffect(() => {
        carregarParceiros();
    }, [filtroCategoria]); // Reload when category filter changes to use server-side filter if desired, or client side. 
    // The controller supports query params: busca, categoria, ativo.
    // For simplicity, let's trigger reload on search or just load all (assuming low volume initially) and filter client side.
    // Given the controller implementation: `exports.listar` uses `req.query`. Let's use server-side filtering.

    const carregarParceiros = async () => {
        setLoading(true);
        try {
            const params = {};
            if (busca) params.busca = busca;
            if (filtroCategoria) params.categoria = filtroCategoria;

            const response = await api.get('/parceiro', { params });
            setParceiros(response.data.dados || []);
        } catch (error) {
            console.error('Erro ao carregar parceiros:', error);
            setError('Erro ao carregar parceiros.');
        } finally {
            setLoading(false);
        }
    };

    // Debounce search
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            carregarParceiros();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [busca]);

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleOpenCadastro = (id = null) => {
        setParceiroEditarId(id);
        setOpenCadastro(true);
    };

    const handleCloseCadastro = () => {
        setOpenCadastro(false);
        setParceiroEditarId(null);
    };

    const handleSaveSuccess = (message) => {
        setSuccess(message);
        carregarParceiros();
        handleCloseCadastro();
    };

    const { user } = useAuth(); // Access user context

    const handlePrint = async (parceiro) => {
        try {
            const [templateRes, empresaRes] = await Promise.all([
                api.get('/contrato-template'),
                user?.id_empresa || user?.idempresa ? api.get(`/empresa/${user?.id_empresa || user?.idempresa}`) : Promise.resolve({ data: {} })
            ]);

            const templates = templateRes.data.dados || [];
            // Procurar template de Rede Credenciada ou Parceiro
            const template = templates.find(t => t.st_titulo.includes('Rede Credenciada') || t.st_titulo.includes('Parceiro'));
            const empresaData = empresaRes.data?.dados || empresaRes.data || {};
            const logoUrl = empresaData.st_logotipo || '';

            if (!template) {
                alert('Modelo de contrato de rede credenciada não encontrado.');
                return;
            }

            let conteudo = template.st_conteudo_html;

            // Formatar Data Atual
            const hoje = new Date();
            const dia = String(hoje.getDate()).padStart(2, '0');
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const ano = hoje.getFullYear();
            const dataAtual = `${dia}/${mes}/${ano}`;

            // Helper para destacar campos
            const highlight = (text) => `<span class="highlight">${text}</span>`;

            // Substituir Placeholders
            conteudo = conteudo.replace(/{{nome}}/g, highlight(parceiro.st_nome || 'N/A'));
            conteudo = conteudo.replace(/{{codigo_credenciado}}/g, highlight(parceiro.st_codigo || 'N/A'));
            conteudo = conteudo.replace(/{{razao_social}}/g, highlight(parceiro.st_nome || 'N/A'));
            conteudo = conteudo.replace(/{{fantasia}}/g, highlight(parceiro.st_fantasia || 'N/A'));

            const docFormatado = parceiro.st_cnpj?.length > 11 ? formatarCNPJ(parceiro.st_cnpj) : formatarCPF(parceiro.st_cnpj);
            conteudo = conteudo.replace(/{{cnpj}}/g, highlight(docFormatado || 'N/A'));
            conteudo = conteudo.replace(/{{cpf}}/g, highlight(docFormatado || 'N/A'));

            const enderecoCompleto = `${parceiro.st_logradouro || ''}, ${parceiro.st_numero || ''} - ${parceiro.st_bairro || ''}`;
            conteudo = conteudo.replace(/{{endereco}}/g, highlight(enderecoCompleto));

            conteudo = conteudo.replace(/{{cidade}}/g, highlight(parceiro.st_cidade || 'Mogi das Cruzes'));
            conteudo = conteudo.replace(/{{data_atual}}/g, highlight(dataAtual));

            // Processar Unidades de Atendimento
            if (parceiro.unidades && parceiro.unidades.length > 0) {
                parceiro.unidades.forEach((unidade, index) => {
                    const num = index + 1;
                    const dadosUnidade = `
                        <strong>Filial ${num}:</strong> ${unidade.st_nome || 'Unidade ' + num}<br/>
                        CNPJ: ${unidade.st_cnpj ? formatarCNPJ(unidade.st_cnpj) : 'N/A'}<br/>
                        Endereço: ${unidade.st_logradouro || ''}, ${unidade.st_numero || ''} - ${unidade.st_bairro || ''}, ${unidade.st_cidade || ''} - ${unidade.st_estado || ''}<br/>
                        Tel: ${unidade.st_telefone ? formatarTelefone(unidade.st_telefone) : 'N/A'}
                    `;
                    conteudo = conteudo.replace(new RegExp(`{{unidade ${num}}}`, 'gi'), highlight(dadosUnidade));
                });
            }

            // Limpar placeholders de unidade não utilizados (ex: {{unidade 2}} se só tiver 1)
            conteudo = conteudo.replace(/{{unidade \d+}}/gi, '');

            const win = window.open('', '_blank');
            win.document.write(`
                <html>
                    <head>
                        <title>Contrato - ${parceiro.st_nome}</title>
                        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Lato:wght@300;400;700&display=swap" rel="stylesheet">
                        <style>
                            @page {
                                size: A4;
                                margin: 0;
                            }
                            body { 
                                font-family: 'Lato', sans-serif;
                                font-size: 11pt;
                                color: #333;
                                line-height: 1.6;
                                margin: 0;
                                padding: 0.5cm;
                                box-sizing: border-box;
                            }

                            /* Container Principal com Borda e Cantos Arredondados */
                            .main-frame {
                                border: 2px solid #20ac9c; /* Use logo primary color */
                                border-radius: 20px;
                                padding: 40px 60px; /* Increased side padding */
                                min-height: 95vh;
                                position: relative;
                                margin: 0 auto;
                                box-sizing: border-box;
                            }

                            /* Header Setup */
                            .header {
                                display: flex;
                                align-items: flex-start;
                                justify-content: space-between;
                                margin-bottom: 5px; /* Reduced margin */
                                border-bottom: 3px solid #D4AF37; /* Gold Accent */
                                padding-bottom: 10px; /* Reduced padding */
                            }
                            .header-logo img {
                                height: 130px; /* Increased logo size */
                                object-fit: contain;
                            }
                            
                            /* Body Styling */
                            .contract-body {
                                text-align: justify;
                                white-space: pre-wrap;
                                overflow-wrap: break-word;
                                word-break: break-word;
                            }
                            .contract-body > *:first-child {
                                margin-top: 0;
                            }
                            
                            /* Gradient Bar Top/Bottom of Frame */
                            .frame-decoration-top {
                                height: 8px;
                                background: linear-gradient(90deg, #20ac9c 0%, #D4AF37 100%);
                                border-radius: 6px 6px 0 0;
                                margin: -40px -60px 30px -60px; /* Pull to edges */
                            }

                            /* Typography Enhancements */
                            strong, b {
                                color: #000;
                                font-weight: 700;
                            }
                            
                            /* Highlighted Fields */
                            .highlight {
                                font-weight: bold;
                                color: #20ac9c; /* Teal Text */
                                border-bottom: 1px dotted #333;
                                padding: 0 2px;
                            }

                            /* Signatures */
                            .signature-section {
                                margin-top: 50px;
                                break-inside: avoid;
                            }
                        </style>
                    </head>
                    <body>
                        <div class="main-frame">
                            <div class="frame-decoration-top"></div>
                            
                            <div class="header">
                                <div class="header-logo">
                                    ${logoUrl ? `<img src="${logoUrl}" alt="Logo" />` : ''}
                                </div>
                            </div>

                            <div class="contract-body">
                                ${conteudo}
                            </div>
                        </div>

                        <script>
                            window.onload = function() { 
                                setTimeout(() => window.print(), 800); 
                            }
                        </script>
                    </body>
                </html>
            `);
            win.document.close();

        } catch (err) {
            console.error(err);
            setError("Erro ao gerar impressão.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Tem certeza que deseja inativar este parceiro?")) return;
        try {
            await api.delete(`/parceiro/${id}`);
            setSuccess("Parceiro inativado com sucesso.");
            carregarParceiros();
        } catch (error) {
            setError("Erro ao inativar parceiro.");
        }
    };

    // Client-side Sort (since we fetched filtered data)
    const parceirosOrdenados = [...parceiros].sort((a, b) => {
        const valA = a[orderBy] || "";
        const valB = b[orderBy] || "";

        if (typeof valA === 'string') {
            return order === 'asc'
                ? valA.localeCompare(valB)
                : valB.localeCompare(valA);
        }
        return order === 'asc' ? valA - valB : valB - valA;
    });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>{success}</Alert>
            </Snackbar>

            <Box sx={{ margin: '0 1.5% 1.5% 1.5%', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2, gap: 2 }}>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <StoreIcon sx={{ color: '#20ac9c', fontSize: 32 }} />
                        Gestão de Parceiros
                    </Typography>
                    <Button variant="contained" onClick={() => handleOpenCadastro()} sx={{ backgroundColor: '#20ac9c' }}>
                        <AddIcon /> Novo Parceiro
                    </Button>
                </Box>

                {/* Filters */}
                <Box sx={{ p: 2, backgroundColor: 'background.paper', mb: 2, borderRadius: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar por nome, fantasia ou CNPJ"
                        value={busca}
                        onChange={(e) => setBusca(e.target.value)}
                        sx={{ minWidth: 300, flex: 1 }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                    />
                    <TextField
                        select
                        size="small"
                        label="Categoria"
                        value={filtroCategoria}
                        onChange={(e) => setFiltroCategoria(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        {categorias.map(cat => (
                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel active={orderBy === "st_codigo"} direction={order} onClick={() => handleSort("st_codigo")}>
                                        Código
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel active={orderBy === "st_nome"} direction={order} onClick={() => handleSort("st_nome")}>
                                        Razão Social
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>Fantasia</TableCell>
                                <TableCell>CNPJ</TableCell>
                                <TableCell>Categoria</TableCell>
                                <TableCell align="center">Unidades</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell align="center">Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={8} align="center"><Preloader /></TableCell></TableRow>
                            ) : parceirosOrdenados.length === 0 ? (
                                <TableRow><TableCell colSpan={8} align="center">Nenhum parceiro encontrado</TableCell></TableRow>
                            ) : (
                                parceirosOrdenados.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((parceiro) => (
                                    <TableRow key={parceiro.id_parceiro} hover>
                                        <TableCell>{parceiro.st_codigo || '-'}</TableCell>
                                        <TableCell>{parceiro.st_nome}</TableCell>
                                        <TableCell>{parceiro.st_fantasia || '-'}</TableCell>
                                        <TableCell>{formatarCNPJ(parceiro.st_cnpj)}</TableCell>
                                        <TableCell>
                                            <Chip label={parceiro.en_categoria} size="small" variant="outlined" />
                                        </TableCell>
                                        <TableCell align="center">
                                            {parceiro.unidades?.length || 0}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={parceiro.bo_ativo ? "Ativo" : "Inativo"}
                                                color={parceiro.bo_ativo ? "success" : "default"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="Editar">
                                                <IconButton onClick={() => handleOpenCadastro(parceiro.id_parceiro)} size="small">
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Imprimir Contrato">
                                                <IconButton onClick={() => handlePrint(parceiro)} size="small">
                                                    <PrintIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Inativar">
                                                <IconButton onClick={() => handleDelete(parceiro.id_parceiro)} size="small" color="error">
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={parceirosOrdenados.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Box>

            {/* Dialog Component */}
            {openCadastro && (
                <CadastroParceiro
                    open={openCadastro}
                    onClose={handleCloseCadastro}
                    parceiroId={parceiroEditarId}
                    onSuccess={handleSaveSuccess}
                />
            )}
        </Box>
    );
}
